import cx from 'classnames'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Component } from 'react'

import {
  isSameDay,
  allDaysDisabledBefore,
  allDaysDisabledAfter,
  getEffectiveMinDate,
  getEffectiveMaxDate
} from './DateUtils'
import Month from './Month'
import MonthDropdown from './MonthDropdown'
import YearDropdown from './YearDropdown'

const DROPDOWN_FOCUS_CLASSNAMES = [
  'react-datepicker__year-select',
  'react-datepicker__month-select'
]

const isDropdownSelect = (element = {}) => {
  const classNames = (element.className || '').split(/\s+/)
  return DROPDOWN_FOCUS_CLASSNAMES.some(
    testClassname => classNames.indexOf(testClassname) >= 0
  )
}

class Calendar extends Component {
  constructor (props) {
    super(props)
    this.state = {
      date: this.localizeMoment(this.getDateInView()),
      selectingDate: null
    }
    this.getDateInView = this.getDateInView.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleDayMouseEnter = this.handleDayMouseEnter.bind(this)
    this.handleMonthMouseLeave = this.handleMonthMouseLeave.bind(this)
    this.handleDayClick = this.handleDayClick.bind(this)
    this.handleDropdownFocus = this.handleDropdownFocus.bind(this)
    this.localizeMoment = this.localizeMoment.bind(this)
    this.increaseMonth = this.increaseMonth.bind(this)
    this.decreaseMonth = this.decreaseMonth.bind(this)
    this.handleDayClick = this.handleDayClick.bind(this)
    this.handleDayMouseEnter = this.handleDayMouseEnter.bind(this)
    this.handleMonthChange = this.handleMonthChange.bind(this)
    this.changeYear = this.changeYear.bind(this)
    this.changeMonth = this.changeMonth.bind(this)
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    const { preSelection, openToDate } = this.props
    if (
      nextProps.preSelection &&
      !isSameDay(nextProps.preSelection, preSelection)
    ) {
      this.setState({
        date: this.localizeMoment(nextProps.preSelection)
      })
    } else if (
      nextProps.openToDate &&
      !isSameDay(nextProps.openToDate, openToDate)
    ) {
      this.setState({
        date: this.localizeMoment(nextProps.openToDate)
      })
    }
  }

  handleClickOutside (event) {
    const { onClickOutside } = this.props
    onClickOutside(event)
  }

  handleDropdownFocus (event) {
    const { onDropdownFocus } = this.props
    if (isDropdownSelect(event.target)) {
      onDropdownFocus()
    }
  }

  getDateInView () {
    const { preSelection, selected, openToDate, utcOffset } = this.props
    const minDate = getEffectiveMinDate(this.props)
    const maxDate = getEffectiveMaxDate(this.props)
    const current = moment.utc().utcOffset(utcOffset)
    const initialDate = preSelection || selected
    if (initialDate) {
      if (typeof initialDate === 'string') {
        return moment(initialDate)
      }
      return initialDate
    }
    if (
      minDate &&
      maxDate &&
      openToDate &&
      openToDate.isBetween(minDate, maxDate)
    ) {
      return openToDate
    }
    if (minDate && openToDate && openToDate.isAfter(minDate)) {
      return openToDate
    }
    if (minDate && minDate.isAfter(current)) {
      return minDate
    }
    if (maxDate && openToDate && openToDate.isBefore(maxDate)) {
      return openToDate
    }
    if (maxDate && maxDate.isBefore(current)) {
      return maxDate
    }
    if (openToDate) {
      return openToDate
    }
    return current
  }

  localizeMoment (date) {
    const { locale } = this.props
    return date.clone().locale(locale || moment.locale())
  }

  increaseMonth () {
    const { date } = this.state
    this.setState(
      s => ({
        date: s.date.clone().add(1, 'month')
      }),
      () => this.handleMonthChange(date)
    )
  }

  decreaseMonth () {
    const { date } = this.state
    this.setState(
      s => ({
        date: s.date.clone().subtract(1, 'month')
      }),
      () => this.handleMonthChange(date)
    )
  }

  handleDayClick (day, event) {
    const { onSelect } = this.props
    onSelect(day, event)
  }

  handleDayMouseEnter (day) {
    this.setState({ selectingDate: day })
  }

  handleMonthMouseLeave () {
    this.setState({ selectingDate: null })
  }

  handleMonthChange (date) {
    const { onMonthChange } = this.props
    if (onMonthChange) {
      onMonthChange(date)
    }
  }

  changeYear (year) {
    this.setState(s => ({
      date: s.date.clone().set('year', year)
    }))
  }

  changeMonth (month) {
    const { date } = this.state
    this.setState(
      s => ({
        date: s.date.clone().set('month', month)
      }),
      () => this.handleMonthChange(date)
    )
  }

  header (date) {
    const { date: defaultDate } = this.state
    const { showWeekNumbers } = this.props
    const startOfWeek = (date || defaultDate).clone().startOf('week')
    const dayNames = []
    if (showWeekNumbers) {
      dayNames.push(
        <div
          key='W'
          className='react-datepicker__day-name'
        >
          #
        </div>
      )
    }
    return dayNames.concat(
      [ 0, 1, 2, 3, 4, 5, 6 ].map(offset => {
        const day = startOfWeek.clone().add(offset, 'days')
        return (
          <div
            key={offset}
            className='react-datepicker__day-name'
          >
            {day.localeData().weekdaysMin(day)}
          </div>
        )
      })
    )
  }

  renderCurrentDateHeader () {
    const { selected, startDate } = this.props
    let select
    if (selected) {
      select = selected
    }
    if (!selected && startDate) {
      select = startDate
    }

    if (select) {
      const year = select.format('YYYY')
      const date = select.format('MMM Do')
      const day = select.format('ddd')
      const dayDate = `${day}, ${date}`
      return (
        <div className='nh-reactdatepicker__selected-date-header'>
          <div className='year-label'>{year}</div>
          <div className='day-date'>{dayDate}</div>
        </div>
      )
    }
    return null
  }

  renderPreviousMonthButton () {
    const { forceShowMonthNavigation, minDate, includeDates } = this.props
    const { date } = this.state
    if (
      !forceShowMonthNavigation &&
      allDaysDisabledBefore(date, 'month', {
        minDate,
        includeDates
      })
    ) {
      return null
    }
    return (
      <button
        aria-hidden='true'
        className='react-datepicker__navigation react-datepicker__navigation--previous'
        onClick={this.decreaseMonth}
      />
    )
  }

  renderNextMonthButton () {
    if (
      !this.props.forceShowMonthNavigation &&
      allDaysDisabledAfter(this.state.date, 'month', this.props)
    ) {
      return null
    }
    return (
      <button
        className='react-datepicker__navigation react-datepicker__navigation--next'
        onClick={this.increaseMonth}
      />
    )
  }

  renderCurrentMonth (date = this.state.date) {
    const classes = [ 'react-datepicker__current-month' ]
    if (this.props.showYearDropdown) {
      classes.push('react-datepicker__current-month--hasYearDropdown')
    }
    if (this.props.showMonthDropdown) {
      classes.push('react-datepicker__current-month--hasMonthDropdown')
    }
    return (
      <div className={classes.join(' ')}>
        {date.format(this.props.dateFormat)}
      </div>
    )
  }

  renderYearDropdown (overrideHide = false) {
    if (!this.props.showYearDropdown || overrideHide) {
      return null
    }
    return (
      <YearDropdown
        dropdownMode={this.props.dropdownMode}
        onChange={this.changeYear}
        minDate={this.props.minDate}
        maxDate={this.props.maxDate}
        year={this.state.date.year()}
        scrollableYearDropdown={this.props.scrollableYearDropdown}
      />
    )
  }

  renderMonthDropdown () {
    if (!this.props.showMonthDropdown) {
      return null
    }
    return (
      <MonthDropdown
        dropdownMode={this.props.dropdownMode}
        locale={this.props.locale}
        onChange={this.changeMonth}
        month={this.state.date.month()}
      />
    )
  }

  renderTodayButton () {
    if (!this.props.todayButton) {
      return null
    }

    return (
      <button
        className='react-datepicker__today-button'
        onClick={event => {
          this.props.onSelect(
            moment.utc().utcOffset(this.props.utcOffset).startOf('date'),
            event
          )
        }}
      >
        {this.props.todayButton}
      </button>
    )
  }

  renderMonths () {
    const monthList = []
    for (let i = 0; i < this.props.monthsShown; ++i) {
      const monthDate = this.state.date.clone().add(i, 'M')
      const monthKey = `month-${i}`
      monthList.push(
        <div
          key={monthKey}
          className='react-datepicker__month-container'
        >
          <div className='react-datepicker__header'>
            {this.renderCurrentMonth(monthDate)}
            <div
              className={`react-datepicker__header__dropdown react-datepicker__header__dropdown--${this.props.dropdownMode}`}
              onFocus={this.handleDropdownFocus}
            >
              {this.renderMonthDropdown(i !== 0)}
              {this.renderYearDropdown(i !== 0)}
            </div>
            <div className='react-datepicker__day-names'>
              {this.header(monthDate)}
            </div>
          </div>
          <Month
            day={monthDate}
            onDayClick={this.handleDayClick}
            onDayMouseEnter={this.handleDayMouseEnter}
            onMouseLeave={this.handleMonthMouseLeave}
            minDate={this.props.minDate}
            maxDate={this.props.maxDate}
            excludeDates={this.props.excludeDates}
            highlightDates={this.props.highlightDates}
            selectingDate={this.state.selectingDate}
            includeDates={this.props.includeDates}
            inline={this.props.inline}
            fixedHeight={this.props.fixedHeight}
            filterDate={this.props.filterDate}
            preSelection={this.props.preSelection}
            selected={this.props.selected}
            selectsStart={this.props.selectsStart}
            selectsEnd={this.props.selectsEnd}
            showWeekNumbers={this.props.showWeekNumbers}
            startDate={this.props.startDate}
            endDate={this.props.endDate}
            peekNextMonth={this.props.peekNextMonth}
            utcOffset={this.props.utcOffset}
          />
        </div>
      )
    }
    return monthList
  }

  render () {
    const { className, children } = this.props
    return (
      <div className={cx('react-datepicker', className)}>
        {this.renderCurrentDateHeader()}
        <div>
          {this.renderPreviousMonthButton()}
          {this.renderNextMonthButton()}
        </div>
        {this.renderMonths()}
        {this.renderTodayButton()}
        {children}
      </div>
    )
  }
}

Calendar.displayName = 'Calendar'

Calendar.defaultProps = {
  utcOffset: moment.utc().utcOffset(),
  monthsShown: 1,
  forceShowMonthNavigation: false,
  onDropdownFocus: () => {
    // Do nothing
  }
}

Calendar.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  dateFormat: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array)
  ]).isRequired,
  dropdownMode: PropTypes.oneOf([ 'scroll', 'select' ]).isRequired,
  endDate: PropTypes.instanceOf(Object),
  excludeDates: PropTypes.instanceOf(Array),
  filterDate: PropTypes.func,
  fixedHeight: PropTypes.bool,
  highlightDates: PropTypes.instanceOf(Array),
  includeDates: PropTypes.instanceOf(Array),
  inline: PropTypes.bool,
  locale: PropTypes.string,
  maxDate: PropTypes.instanceOf(Object),
  minDate: PropTypes.instanceOf(Object),
  monthsShown: PropTypes.number,
  onClickOutside: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func,
  forceShowMonthNavigation: PropTypes.bool,
  onDropdownFocus: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  openToDate: PropTypes.instanceOf(Object),
  peekNextMonth: PropTypes.bool,
  scrollableYearDropdown: PropTypes.bool,
  preSelection: PropTypes.instanceOf(Object),
  selected: PropTypes.instanceOf(Object),
  selectsEnd: PropTypes.bool,
  selectsStart: PropTypes.bool,
  showMonthDropdown: PropTypes.bool,
  showWeekNumbers: PropTypes.bool,
  showYearDropdown: PropTypes.bool,
  startDate: PropTypes.instanceOf(Object),
  todayButton: PropTypes.string,
  utcOffset: PropTypes.number
}

export default Calendar
